import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Spinner,
  Center,
  useToast,
  Badge,
  IconButton,
  Text,
  Flex,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { EditIcon, DeleteIcon, AddIcon } from "@chakra-ui/icons";
import { supabase } from "../../../lib/supabaseClient";

const NoticeList = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("notice")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setNotices(data || []);
    } catch (error) {
      console.error("공지사항 조회 실패:", error);
      toast({
        title: "공지사항을 불러오는데 실패했습니다",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (noticeId) => {
    navigate(`/admin/notice/edit/${noticeId}`);
  };

  const handleDelete = async (noticeId) => {
    if (!window.confirm("정말로 이 공지사항을 삭제하시겠습니까?")) return;

    try {
      const { error } = await supabase
        .from("notice")
        .delete()
        .eq("id", noticeId);

      if (error) throw error;

      toast({
        title: "공지사항이 삭제되었습니다",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      fetchNotices();
    } catch (error) {
      console.error("공지사항 삭제 실패:", error);
      toast({
        title: "공지사항 삭제에 실패했습니다",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Center minH="60vh">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" p={6}>
      <Container maxW="container.xl">
        <Flex justify="space-between" align="center" mb={6}>
          <Heading size="lg" color="gray.800">
            공지사항 관리
          </Heading>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={() => navigate("/admin/notice/create")}
          >
            새 공지사항 등록
          </Button>
        </Flex>

        <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>제목</Th>
                <Th>상태</Th>
                <Th>내용 미리보기</Th>
                <Th>등록일</Th>
                <Th>작업</Th>
              </Tr>
            </Thead>
            <Tbody>
              {notices.length === 0 ? (
                <Tr>
                  <Td colSpan={5} textAlign="center" py={8}>
                    <Text color="gray.500">등록된 공지사항이 없습니다</Text>
                  </Td>
                </Tr>
              ) : (
                notices.map((notice) => (
                  <Tr key={notice.id} _hover={{ bg: "gray.50" }}>
                    <Td>
                      <Text fontWeight="medium" noOfLines={1}>
                        {notice.title}
                      </Text>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={notice.is_visible ? "green" : "gray"}
                        variant="subtle"
                      >
                        {notice.is_visible ? "표시" : "숨김"}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontSize="sm" color="gray.600" noOfLines={2}>
                        {notice.content
                          ? notice.content.substring(0, 100) + "..."
                          : "내용 없음"}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm" color="gray.600">
                        {formatDate(notice.created_at)}
                      </Text>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          icon={<EditIcon />}
                          size="sm"
                          colorScheme="blue"
                          variant="ghost"
                          onClick={() => handleEdit(notice.id)}
                          aria-label="수정"
                        />
                        <IconButton
                          icon={<DeleteIcon />}
                          size="sm"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => handleDelete(notice.id)}
                          aria-label="삭제"
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      </Container>
    </Box>
  );
};

export default NoticeList;
